import { LiveAnnouncer } from '@angular/cdk/a11y';
import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet, TitleStrategy } from '@angular/router';
import { filter } from 'rxjs';
import { BsiteTitleStrategy } from '../../core/title/bsite-title.strategy';
import { BottomNavComponent } from '../bottom-nav/bottom-nav.component';
import { MobileDrawerComponent } from '../mobile-drawer/mobile-drawer.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { TopbarComponent } from '../topbar/topbar.component';
import { ViewportService } from './viewport.service';

/**
 * Struttura comune delle pagine interne (FR-020): barra laterale, testata e contenuto. Da 1024
 * px la barra laterale è fissa e larga 272 px; sotto diventa un pannello apribile, e sotto i
 * 768 px compare la barra in basso. Il collegamento "Vai al contenuto" è il primo elemento
 * focalizzabile e ogni cambio di pagina si annuncia ai lettori di schermo (FR-031).
 */
@Component({
  selector: 'bs-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    SidebarComponent,
    TopbarComponent,
    BottomNavComponent,
    MobileDrawerComponent,
  ],
  host: { class: 'block min-h-dvh bg-white dark:bg-stone-950' },
  template: `
    <a
      href="#content"
      data-testid="skip-link"
      class="sr-only z-50 rounded-full bg-bsite-400 px-4 py-2 text-sm font-semibold text-ink focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      (click)="skipToContent($event)"
      >Vai al contenuto</a
    >

    @if (viewport.isDesktop()) {
      <div class="fixed inset-y-0 left-0 z-10 flex w-68 flex-col">
        <bs-sidebar />
      </div>
    } @else {
      <bs-mobile-drawer [open]="drawerOpen()" (closed)="closeDrawer()">
        <bs-sidebar [inDrawer]="true" (closeRequested)="closeDrawer()" />
      </bs-mobile-drawer>
    }

    <div class="flex min-h-dvh min-w-0 flex-col" [class.pl-68]="viewport.isDesktop()">
      <bs-topbar [menuExpanded]="drawerOpen()" (menuRequested)="openDrawer()" />
      <main
        id="content"
        tabindex="-1"
        class="min-w-0 flex-1 px-4 pb-10 pt-4 outline-none md:px-8 lg:px-10"
        [class.pb-28]="viewport.isMobile()"
      >
        <router-outlet />
      </main>
    </div>

    @if (viewport.isMobile()) {
      <bs-bottom-nav [moreExpanded]="drawerOpen()" (moreRequested)="openDrawer()" />
    }
  `,
})
export class ShellComponent {
  protected readonly viewport = inject(ViewportService);
  protected readonly drawerOpen = signal(false);
  private readonly document = inject(DOCUMENT);
  private readonly announcer = inject(LiveAnnouncer);
  private readonly title = inject(TitleStrategy);

  constructor() {
    const router = inject(Router);
    const subscription = router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.drawerOpen.set(false));
    inject(DestroyRef).onDestroy(() => subscription.unsubscribe());

    // Il pannello non serve più quando la barra laterale torna fissa.
    effect(() => {
      if (this.viewport.isDesktop()) {
        untracked(() => this.drawerOpen.set(false));
      }
    });

    // Annuncio del cambio di pagina, con il titolo (FR-031).
    if (this.title instanceof BsiteTitleStrategy) {
      const strategy = this.title;
      let first = true;
      effect(() => {
        const page = strategy.pageTitle();
        if (first) {
          first = false;
          return;
        }
        if (page) {
          void this.announcer.announce(`Pagina: ${page}`, 'polite');
        }
      });
    }
  }

  protected openDrawer(): void {
    this.drawerOpen.set(true);
  }

  protected closeDrawer(): void {
    this.drawerOpen.set(false);
  }

  protected skipToContent(event: Event): void {
    event.preventDefault();
    this.document.getElementById('content')?.focus();
  }
}
